import { Subscription, UserType } from 'lib.store/src/models-to-replace/auth';
import { formatByCurrency } from 'lib.shared/src/numberFormat';
import { formatDateUtc } from 'lib.shared/src/utils';
import { SubscriptionName } from 'lib.store/src/main/subscription/models';
import { BillingInfo } from 'lib.store/src/main/billing/models';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';

export type TopUpBonusTier = {
  amount: number;
  months: number;
};

export type ProSubscriptionEnhanced = Subscription & {
  percent: number;
  statusName: string;
  formattedDate: string;
  formattedDay: string;
  formattedPrice: string;
  formattedSpendForFree: string;
  formattedTopUpBonusAmount: string;
  spend_for_free: number;
  top_up_bonus?: TopUpBonusTier[];
  limit: number;
};

export type ProSubscriptionData = {
  proSubscriptions: ProSubscriptionEnhanced[];
  /** the organization's one PRO — the active row when there is one */
  proSubscription: ProSubscriptionEnhanced | null;
  isProEnabled: boolean;
  /** the launch gate — an active PRO, or a billing mode PRO does not apply to */
  hasProAccess: boolean;
  isProVisible: boolean;
  /** the monthly PRO price in the billing currency, "" until billing info loads */
  formattedProPrice: string;
  /** the credits PRO grants each month (the plan's config), null until billing info loads */
  proMonthlyCredits: number | null;
};

const getProSubscription = (user?: UserType, billingInfo?: BillingInfo): ProSubscriptionData => {
  const { organization } = user || {};
  if (!user || organization?.type === ORGANIZATION_TYPE.organizer) {
    return {
      proSubscriptions: [],
      proSubscription: null,
      isProEnabled: false,
      hasProAccess: false,
      isProVisible: false,
      formattedProPrice: '',
      proMonthlyCredits: null,
    };
  }

  let subscriptions = organization?.subscriptions || [];
  if (!Array.isArray(organization?.subscriptions)) {
    subscriptions = organization?.subscriptions ? [organization.subscriptions] : [];
  }

  const billingInfoProSubscription = billingInfo?.subscriptions?.find(
    ({ plan }) => plan?.name === SubscriptionName.boost_pro,
  );
  const {
    spend_for_free = 0,
    currency: billingInfoCurrency,
    price = 200,
    top_up_bonus,
    credits: proMonthlyCredits = null,
  } = billingInfoProSubscription?.money || {};
  const formattedTopUpBonusAmount =
    top_up_bonus && top_up_bonus.length > 0
      ? formatByCurrency(top_up_bonus[0].amount, billingInfoCurrency)
      : '';

  const proSubscriptions = subscriptions
    .filter(({ type }: Subscription) => type?.name === SubscriptionName.boost_pro)
    .map((subscription) => {
      const { status, date = '', money } = subscription;
      const { name } = status || {};
      const { spend = 0, limit = 100, currency } = money || {};
      const percent = (spend * 100) / limit;
      const statusName = name ? `${name[0].toUpperCase()}${name.slice(1)}` : '';
      const formattedDate = formatDateUtc(date);
      const formattedDay = formattedDate?.split(',')?.[0] || '';

      return {
        ...subscription,
        percent,
        statusName,
        formattedDate,
        formattedDay,
        formattedPrice: formatByCurrency(price, billingInfoCurrency, 0),
        formattedSpendForFree: formatByCurrency(spend_for_free, billingInfoCurrency, 0),
        formattedTopUpBonusAmount,
        spend_for_free,
        top_up_bonus,
        limit,
        money: {
          ...(subscription.money || {}),
          spend,
          limit,
          currency,
          price,
        },
      };
    });

  return {
    proSubscriptions,
    proSubscription: proSubscriptions.find((s) => s.is_active) || proSubscriptions[0] || null,
    isProEnabled: proSubscriptions.some((s) => s.is_active),
    // the launch gate, as the backend decides it: an active PRO, or a billing
    // mode PRO does not apply to (isProEnabled is about the subscription row)
    hasProAccess: organization?.has_pro_access ?? proSubscriptions.some((s) => s.is_active),
    isProVisible: organization?.type === 'partner',
    formattedProPrice: billingInfoProSubscription
      ? formatByCurrency(price, billingInfoCurrency, 0)
      : '',
    proMonthlyCredits: proMonthlyCredits || null,
  };
};

export default getProSubscription;
